import { createSelector } from 'reselect'
import { Apartment, BeforeLoginApartment } from '../types/apartments'
import { StateInterface } from '../types/states'

/** main selector */
const selectApartmentsDomain = (state: StateInterface) => state.apartments

// base apartment selectors
const selectApartments = createSelector(
  selectApartmentsDomain,
  (apartments) => apartments.apartments
)
/** often easier to handle than an object map */
const selectApartmentsAsArray =
  createSelector(
    selectApartments,
    (apartments) => Object.values(apartments || {}) as Apartment[]
  ) || ([] as Apartment[])

const selectApartmentsFiles = createSelector(
  selectApartmentsDomain,
  (apartments) => apartments.files || {}
)
const selectIsApartmentsLoading = createSelector(
  selectApartmentsDomain,
  (apartments) => apartments.isApartmentsLoading
)
const selectIsApartmentsSearchLoading = createSelector(
  selectApartmentsDomain,
  (apartments) => apartments.isApartmentsSearchLoading
)
const selectSelectedApartmentId = createSelector(
  selectApartmentsDomain,
  (apartments) => apartments.selectedApartmentId as string
)

// before login apartment selectors
const selectBeforeLoginApartment = createSelector(
  selectApartmentsDomain,
  (apartments) =>
    apartments.beforeLoginApartment as BeforeLoginApartment | undefined
)

const selectInstantRentApplication = createSelector(
  selectApartmentsDomain,
  (apartments) =>
    apartments.beforeLoginApartment as BeforeLoginApartment | undefined
)

const selectBeforeLoginMedia = createSelector(
  selectApartmentsDomain,
  (apartments) => apartments.beforeLoginMedia
)

// apartment selectors by apartment id
const selectApartmentById = (apartmentId: string) =>
  createSelector(
    selectApartments,
    (apartments) => apartments[apartmentId] as Apartment | undefined
  )

const selectApartmentByCode = createSelector(
  selectApartmentsDomain,
  (apartments) => apartments.publicApartment as Apartment | null
)

const selectApartmentMetadataById = (apartmentId: string) =>
  createSelector(
    selectApartments,
    (apartments) => apartments[apartmentId].metadata
  )

const selectApartmentFilesByApartmentId = (apartmentId: string) =>
  createSelector(selectApartmentsFiles, (files) => files[apartmentId])

const selectApartmentsWithoutClientInfo = createSelector(
  selectApartments,
  (apartments) => {
    const apartmentsWithoutClientInfo = Object.values(apartments).filter(
      ({ client, metadata }: Apartment) => !client && metadata?.confirmed
    )
    return apartmentsWithoutClientInfo
  }
)

const selectApartmentsWithoutClientInfoByUserId = (apartmentUserId: string) =>
  createSelector(selectApartments, (apartments) => {
    const apartmentsWithoutClientInfo = Object.values(apartments).filter(
      ({ userId, client, metadata }: Apartment) =>
        userId === apartmentUserId &&
        !client &&
        metadata?.confirmed &&
        (metadata.createdBeforeLogin || metadata.createdOnLogin)
    )
    return apartmentsWithoutClientInfo
  })

const selectHasApartmentsWithoutClientInfo = (apartmentUserId: string) =>
  createSelector(
    selectApartmentsWithoutClientInfoByUserId(apartmentUserId),
    (apartmentsWithoutClientInfo) => apartmentsWithoutClientInfo.length > 0
  )

export {
  selectApartmentsDomain,
  selectApartmentsAsArray,
  selectApartments,
  selectApartmentByCode,
  selectIsApartmentsLoading,
  selectIsApartmentsSearchLoading,
  selectSelectedApartmentId,
  selectBeforeLoginApartment,
  selectInstantRentApplication,
  selectBeforeLoginMedia,
  selectApartmentById,
  selectApartmentMetadataById,
  selectApartmentFilesByApartmentId,
  selectApartmentsWithoutClientInfo,
  selectApartmentsWithoutClientInfoByUserId,
  selectHasApartmentsWithoutClientInfo
}
